// Read-aloud, using the browser's own SpeechSynthesis.
//
// ENTIRELY LOCAL. SpeechSynthesis speaks through the operating system's installed
// voices: no audio is produced remotely and no answer text is transmitted. That is
// specifically NOT true of the speech-to-text half of the Web Speech API, where
// Chrome and Edge send microphone audio to a remote service — the plan document
// had claimed the privacy property for both, which is why this file says so
// plainly (Finding #108).
//
// Nothing here is required for the app to work: every entry point degrades to a
// no-op on a browser without support, and the caller checks isSupported() before
// showing a button at all.

function isSupported() {
  return typeof window !== 'undefined'
    && 'speechSynthesis' in window
    && typeof window.SpeechSynthesisUtterance === 'function';
}

/**
 * Turn a Markdown answer into something worth listening to.
 *
 * THIS IS THE PART THAT MATTERS. Answers now routinely contain pipe tables,
 * bold, headings and [Source 2] citations. Handed to a speech engine unmodified,
 * a table is read as "pipe Debt service pipe six one five comma zero zero zero
 * pipe" and the separator row as forty consecutive hyphens. The transform below
 * reads a table the way a person would: the header cells once, then each row as
 * comma-separated values.
 */
function markdownToSpeech(markdown) {
  const lines = String(markdown || '').split('\n');
  const out = [];
  let headers = null;

  const cells = line => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
  const isSeparator = line => /^\|[\s:|-]+\|?$/.test(line.trim()) && line.includes('-');
  const isRow = line => line.trim().startsWith('|') && line.trim().length > 1;

  const clean = t => t
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/\*{2,}/g, '')                        // stray '****' from an empty bold cell
    .replace(/_{2,}/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')       // links: keep the label, drop the URL
    .replace(/\[(Source[^\]]*)\]/gi, '$1')          // "[Source 2]" -> "Source 2"; the brackets are for the eye
    .replace(/\s{2,}/g, ' ')
    .trim();

  for (const raw of lines) {
    const line = raw.trim();

    if (!line) { out.push(''); headers = null; continue; }

    if (isSeparator(line)) continue;               // never spoken

    if (isRow(line)) {
      const values = cells(line).map(clean);
      if (!headers) {
        headers = values;
        out.push(`Table with columns: ${values.filter(Boolean).join(', ')}.`);
      } else {
        // Pair each value with its column name where there is one — "Cost centre
        // Debt service, FY25 actual 615,000" is followable by ear; a bare list of
        // numbers is not.
        const spoken = values.map((v, i) => {
          if (!v || v === '—') return null;
          const h = headers[i];
          return h ? `${h} ${v}` : v;
        }).filter(Boolean).join(', ');
        if (spoken) out.push(`${spoken}.`);
      }
      continue;
    }

    headers = null;

    // Headings: spoken as a sentence, without the hashes.
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading) { out.push(`${clean(heading[1])}.`); continue; }

    // List items: the bullet itself is noise.
    const bullet = line.match(/^\s*(?:[-*+]|\d+[.)])\s+(.*)$/);
    if (bullet) { out.push(`${clean(bullet[1])}.`); continue; }

    out.push(clean(line));
  }

  return out
    .join(' ')
    .replace(/\s*\.\s*\./g, '.')     // doubled full stops from the row/line joins
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Prefer a local English voice. A remote/network voice would undo the reason for
// choosing SpeechSynthesis in the first place, so localService wins over quality.
function pickVoice() {
  try {
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    const english = voices.filter(v => /^en(-|_|$)/i.test(v.lang || ''));
    const pool = english.length ? english : voices;
    return pool.find(v => v.localService) || pool[0] || null;
  } catch {
    return null;
  }
}

/**
 * Speak text. Returns true if speaking actually started.
 *
 * onEnd fires on natural completion, on cancel, and on error — the caller uses it
 * to clear its "speaking" state, and a button stuck in the speaking state would be
 * worse than no button.
 */
function speak(markdown, { onEnd } = {}) {
  if (!isSupported()) return false;
  const text = markdownToSpeech(markdown);
  if (!text) return false;

  try {
    // Only one thing speaks at a time; starting a new answer cancels the old.
    window.speechSynthesis.cancel();

    const utterance = new window.SpeechSynthesisUtterance(text);
    const voice = pickVoice();
    if (voice) { utterance.voice = voice; utterance.lang = voice.lang; }
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    const finish = () => { if (typeof onEnd === 'function') onEnd(); };
    utterance.onend = finish;
    utterance.onerror = finish;

    window.speechSynthesis.speak(utterance);
    return true;
  } catch {
    return false;
  }
}

function stop() {
  if (!isSupported()) return;
  try { window.speechSynthesis.cancel(); } catch { /* nothing to stop */ }
}

export { isSupported, speak, stop, markdownToSpeech, pickVoice };
